import { useEffect, useState, useMemo } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

type PaymentStatus = 'succeeded' | 'processing' | 'requires_payment_method' | 'default' | 'loading';

const ERROR_MESSAGES = {
  HTTP_409:
    'Payment initialization failed. This may be due to an existing payment in progress or insufficient balance.',
  HTTP_400: 'Invalid payment request. Please check the payment amount and try again.',
  HTTP_401: 'Authentication required. Please log in again.',
  HTTP_403: 'Access denied. You may not have permission to perform this action.',
  HTTP_500: 'Server error. Please try again later.',
  MISSING_SUPPLIER_PAYMENT_ID: 'Invalid response: missing supplierPaymentId',
  MISSING_CLIENT_SECRET: 'Invalid response: missing paymentIntentClientSecret',
  UNKNOWN_ERROR: 'An error occurred while initializing payment',
  SETUP_ERROR: 'An error occurred while setting up Stripe',
  COMPLETION_ERROR: 'An error occurred while completing the checkout',
};

const getErrorMessage = (error: string, statusCode?: number): string => {
  if (statusCode) {
    switch (statusCode) {
      case 409:
        return ERROR_MESSAGES.HTTP_409;
      case 400:
        return ERROR_MESSAGES.HTTP_400;
      case 401:
        return ERROR_MESSAGES.HTTP_401;
      case 403:
        return ERROR_MESSAGES.HTTP_403;
      default:
        if (statusCode >= 500) {
          return ERROR_MESSAGES.HTTP_500;
        } else {
          return `HTTP error! status: ${statusCode}`;
        }
    }
  } else {
    return error;
  }
};

interface StripeSetupResponse {
  stripePublicApiKey: string;
}

interface StripeCheckoutSubmission {
  paymentIntentClientSecret: string;
  paymentIntentId: { id: string };
  supplierPaymentId: { id: number };
}

interface UseStripePaymentStatusProps {
  supplierId: number;
}

interface UseStripePaymentStatusReturn {
  status: PaymentStatus;
  intentId: string | null;
  isLoading: boolean;
}

interface UseStripeCheckoutSubmissionProps {
  closingBalance: number;
  isCc?: boolean;
}

interface UseStripeCheckoutSubmissionReturn {
  clientSecret: string;
  supplierPaymentId: string;
  isLoading: boolean;
  error: string | null;
}

interface UseStripeSetupProps {
  supplierId: number;
}

interface UseStripeSetupReturn {
  stripePromise: Promise<Stripe | null> | null;
  clientSecret: string | undefined;
  isLoading: boolean;
  error: string | null;
}

async function postCompleteStripeCheckout(
  supplierId: number,
  supplierPaymentId: string,
  paymentIntentId: string,
): Promise<any> {
  try {
    const response = await fetch(
      `/api/supplier/${supplierId}/payment/${supplierPaymentId}/complete-stripe-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentIntentId }),
      },
    );

    if (!response.ok) {
      const errorMessage = getErrorMessage('', response.status);
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (err) {
    console.error(err);
    throw err;
  }
}

async function setupStripe(supplierId: number): Promise<StripeSetupResponse> {
  try {
    const response = await fetch(`/api/supplier/${supplierId}/payment/initialise-stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorMessage = getErrorMessage('', response.status);
      throw new Error(errorMessage);
    }

    const data: StripeSetupResponse = await response.json();
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

async function initialiseStripeCheckoutSubmission(
  closingBalance: number,
  isCc: boolean = false,
): Promise<StripeCheckoutSubmission> {
  try {
    const response = await fetch(
      App.Api.wsSupplierUrl(`/payment/initialise-stripe-checkout-submission`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentAmount: closingBalance,
          paymentMethodType: isCc ? undefined : 'IDEAL_INTENT',
        }),
      },
    );

    if (!response.ok) {
      const errorMessage = getErrorMessage('', response.status);
      throw new Error(errorMessage);
    }

    const data: StripeCheckoutSubmission = await response.json();
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export const useStripePaymentStatus = ({
  supplierId,
}: UseStripePaymentStatusProps): UseStripePaymentStatusReturn => {
  const stripe = useStripe();
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [intentId, setIntentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!stripe) {
      setIsLoading(false);
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret',
    );

    if (!clientSecret) {
      setStatus('default');
      setIsLoading(false);
      return;
    }

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        if (!paymentIntent) {
          setStatus('default');
          setIsLoading(false);
          return;
        }

        const newStatus = (paymentIntent.status as PaymentStatus) ?? 'default';
        const newIntentId = paymentIntent.id;
        const supplierPaymentId = new URLSearchParams(window.location.search).get(
          'supplier_payment_id',
        );

        setStatus(newStatus);
        setIntentId(newIntentId);
        setIsLoading(false);

        if (newStatus === 'succeeded' && newIntentId && supplierPaymentId) {
          postCompleteStripeCheckout(supplierId, supplierPaymentId, newIntentId);
        }
      })
      .catch((error) => {
        console.error('Error retrieving payment intent:', error);
        setStatus('default');
        setIsLoading(false);
      });
  }, [stripe, supplierId]);

  return {
    status,
    intentId,
    isLoading,
  };
};

export const useStripeCheckoutSubmission = ({
  closingBalance,
  isCc = false,
}: UseStripeCheckoutSubmissionProps): UseStripeCheckoutSubmissionReturn => {
  const [clientSecret, setClientSecret] = useState<string>('');
  const [supplierPaymentId, setSupplierPaymentId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSecret = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const initStripeSubmissionResponse = await initialiseStripeCheckoutSubmission(
          closingBalance,
          isCc,
        );

        if (!initStripeSubmissionResponse.supplierPaymentId?.id) {
          throw new Error(ERROR_MESSAGES.MISSING_SUPPLIER_PAYMENT_ID);
        }

        if (!initStripeSubmissionResponse.paymentIntentClientSecret) {
          throw new Error(ERROR_MESSAGES.MISSING_CLIENT_SECRET);
        }

        const supplierPaymentId = initStripeSubmissionResponse.supplierPaymentId.id;
        const secret = initStripeSubmissionResponse.paymentIntentClientSecret;

        setClientSecret(secret);
        setSupplierPaymentId(String(supplierPaymentId));
      } catch (err) {
        console.error('Error fetching payment intent:', err);
        const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.UNKNOWN_ERROR;
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSecret();
  }, [closingBalance, isCc]);

  return {
    clientSecret,
    supplierPaymentId,
    isLoading,
    error,
  };
};

export const useSetupStripe = ({ supplierId }: UseStripeSetupProps): UseStripeSetupReturn => {
  const [stripeKey, setStripeKey] = useState<string | undefined>(undefined);
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const stripePromise = useMemo(() => {
    if (!stripeKey) return null;
    return loadStripe(stripeKey);
  }, [stripeKey]);

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { stripePublicApiKey } = await setupStripe(supplierId);
        setStripeKey(stripePublicApiKey);

        const clientSecret = new URLSearchParams(window.location.search).get(
          'payment_intent_client_secret',
        );
        if (clientSecret) {
          setClientSecret(clientSecret);
        }
      } catch (err) {
        console.error('Error fetching Stripe setup:', err);
        const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.SETUP_ERROR;
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApiKey();
  }, [supplierId]);

  return {
    stripePromise,
    clientSecret,
    isLoading,
    error,
  };
};

export { setupStripe, initialiseStripeCheckoutSubmission };
export type { PaymentStatus, StripeSetupResponse, StripeCheckoutSubmission };
